import { ReactNode, useRef } from 'react'
import { styled, SxProps } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { HotSpot } from './hot-spot'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { CommonOnboarding, IOnboarding } from '$logic/onboarding/types'
import { showFeatureOnboardingDialog } from '$comp/modals/feature-onboarding/feature-onboarding-dialog.api'

export type HotspotPosition = 'bottom' | 'right' | 'left' | 'top'
export type HotspotPositionObject = {
  bottom?: string
  right?: string
  left?: string
  top?: string
}

interface WithOnboardingProps {
  children: ReactNode
  onboarding?: CommonOnboarding
  hotspotPosition?: HotspotPositionObject
  onClick?: () => void
  hotspotSize?: 'small' | 'medium'
  className?: string
  sx?: SxProps
  hasFeatureOnboarding?: boolean
  shouldCompleteOnboarding?: boolean
  openCurrentModal?: () => void
}

const PositionedContainer = styled('div')`
  position: relative;
  width: fit-content;

  .hotspot {
    position: absolute;
    z-index: 1000;
  }
`

export function WithOnboarding({
  children,
  onboarding,
  hotspotPosition = {
    top: '-8px',
    left: '-8px',
  },
  onClick,
  hotspotSize = 'medium',
  className,
  sx,
  hasFeatureOnboarding,
  shouldCompleteOnboarding = true,
  openCurrentModal,
}: WithOnboardingProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { enabled, completeOnboarding } =
    useConditionalHook(!!onboarding, {
      hook: useOnboarding,
      hookArgs: onboarding as IOnboarding,
      defaultValue: undefined,
    }) || {}

  const handleClick = async () => {
    if (hasFeatureOnboarding && enabled && onboarding) {
      if (openCurrentModal) await closeCurrentModal()
      showFeatureOnboardingDialog({ feature: onboarding }).then(() => {
        if (shouldCompleteOnboarding) {
          completeOnboarding?.()
          openCurrentModal?.()
        }
      })
      return
    }
    if (onClick) {
      onClick()
      if (shouldCompleteOnboarding) {
        completeOnboarding?.()
      }
    }
  }

  return (
    <PositionedContainer sx={sx} className={className} onClick={handleClick}>
      {children}
      {enabled && (
        <HotSpot
          sx={{ ...hotspotPosition }}
          ref={containerRef}
          containerRef={containerRef}
          size={hotspotSize}
          className="hotspot"
        />
      )}
    </PositionedContainer>
  )
}
